import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import { loadWords } from "./api/words";
import { router } from "./router";
import "./styles.css";

// ⚠ TanStack Query здесь — **буфер транспорта, а не кэш чисел** (`interfaces.md`
// §2.1, оговорка владельца к стеку). Деньги считает ядро при обращении; клиент
// не складывает суммы и не показывает вчерашнее число как сегодняшнее, поэтому
// свежесть по умолчанию нулевая, а повтор запроса при возврате в окно включает
// та поверхность, которой это нужно, и своим решением.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = document.getElementById("root");
if (root === null) {
  throw new Error("index.html: немає елемента #root — нема куди малювати");
}

/**
 * Слова, когда словаря нет вовсе: сервер не отдал `/api/screen-words`.
 *
 * ⚠⚠ **Единственная фраза экрана, которая живёт в клиенте, — и это названное
 * исключение из `G117`, а не забытая строка** (таск 74). Словарь приходит с
 * сервера; если он не пришёл, сказать об этом словами словаря нечем, а экран с
 * кодами вместо слов хуже одной честной фразы. Сторож исключения —
 * `tests/words-in-dictionary.test.ts`.
 */
const WORDS_UNREACHABLE = "Не вдалося відкрити систему: сервер не відповів. Оновіть сторінку.";

const app = createRoot(root);

// ⚠ Словарь — до первого экрана: разметка читает слово синхронно, и экран без
// словаря напечатал бы коды. Пока словарь идёт, корень помечен «занято» для чтеца.
root.setAttribute("aria-busy", "true");
// Пока словаря нет, экран не пуст: полоса шапки и скелет страницы без слов (слов
// ещё нет — сказать нечем). Ответ словаря ждёт той же очереди сервера, что и
// расчёт «Вільно»: живой смоук таска 74 поймал пустой экран на несколько секунд.
app.render(
  <div className="min-h-screen bg-canvas" aria-hidden="true">
    <div className="min-h-header border-b border-line bg-raised" />
    <div className="mx-auto flex max-w-page flex-col gap-md px-md py-xl">
      <div className="h-md w-3xl rounded bg-skeleton" />
      <div className="h-2xl w-full max-w-form rounded bg-skeleton" />
    </div>
  </div>,
);
void loadWords().then((ok) => {
  root.removeAttribute("aria-busy");
  if (!ok) {
    app.render(
      <p role="alert" className="mx-auto max-w-form p-xl text-sm text-ink">
        {WORDS_UNREACHABLE}
      </p>,
    );
    return;
  }
  app.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
